import { BellOff, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useMatch } from "react-router";
import { Avatar } from "@/components/ui/controls";
import { Tip } from "@/components/ui/overlay";
import { cn } from "@/lib/utils";
import { openConversation, useAlertPrefs, useChatHeads, type ChatHead } from "./alerts";
import { useChatStore } from "./socket";

/** Quanto tempo a mensagem fica aberta ao lado da foto antes de recolher. */
const OPEN_MS = 6_500;
const SIZE = 48;
/** Distância entre as fotos abertas e na pilha recolhida. */
const GAP_OPEN = SIZE + 12;
const GAP_STACKED = 14;

/**
 * Balões de mensagem nova no canto inferior esquerdo, como "chat heads": a foto de quem mandou
 * chega flutuando com as primeiras palavras ao lado; depois a mensagem recolhe e as fotos ficam
 * empilhadas, boiando de leve. Passar o mouse abre a pilha; o clique abre a conversa.
 */
export function ChatHeads() {
  const heads = useChatHeads((s) => s.heads);
  const dismiss = useChatHeads((s) => s.dismiss);
  const enabled = useAlertPrefs((s) => s.bubbles);
  const activeId = useChatStore((s) => s.activeId);
  const [fanned, setFanned] = useState(false);
  // No projeto, ao lado da barra de ferramentas (sem cobrir os botões dela); fora, no canto.
  const inProject = Boolean(useMatch("/p/:projectId"));
  // Abriu a conversa (aqui ou pela lista): o balão dela sai.
  useEffect(() => {
    if (activeId) dismiss(activeId);
  }, [activeId, dismiss]);
  if (!enabled || !heads.length) return null;
  // O mais novo fica embaixo, na frente.
  const ordered = [...heads].reverse();
  const stacked = ordered.length > 1 && !fanned;
  const height = stacked ? SIZE + (ordered.length - 1) * GAP_STACKED : SIZE + (ordered.length - 1) * GAP_OPEN;
  return (
    <div
      className={cn(
        "fixed bottom-11 z-[75] w-[360px] max-w-[calc(100vw-32px)] transition-[height] duration-300",
        inProject ? "left-[72px]" : "left-4",
      )}
      style={{ height }}
      onPointerEnter={() => setFanned(true)}
      onPointerLeave={() => setFanned(false)}
      aria-live="polite"
      aria-label="Mensagens novas"
    >
      {ordered.map((h, i) => (
        <Head
          // A chave muda a cada mensagem: a foto "pula" de novo e a mensagem reabre.
          key={`${h.conversationId}:${h.at}`}
          head={h}
          index={i}
          bottom={stacked ? i * GAP_STACKED : i * GAP_OPEN}
          behind={stacked && i > 0}
          fanned={fanned}
        />
      ))}
      {fanned && ordered.length > 1 ? (
        <button
          type="button"
          onClick={() => useChatHeads.setState({ heads: [] })}
          className="absolute left-0 flex h-6 animate-fade-in items-center gap-1 rounded-full border border-border-strong bg-surface px-2.5 text-[11px] text-fg-muted shadow-lg hover:text-fg"
          style={{ bottom: height + 16 }}
        >
          <X className="h-3 w-3" /> Fechar todos
        </button>
      ) : null}
    </div>
  );
}

function Head({
  head,
  index,
  bottom,
  behind,
  fanned,
}: {
  head: ChatHead;
  index: number;
  bottom: number;
  behind: boolean;
  fanned: boolean;
}) {
  const dismiss = useChatHeads((s) => s.dismiss);
  const setPrefs = useAlertPrefs((s) => s.set);
  const [fresh, setFresh] = useState(true);
  const [hover, setHover] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setFresh(false), OPEN_MS);
    return () => clearTimeout(t);
  }, []);
  // A mensagem aparece ao chegar, ao passar o mouse na foto e na pilha aberta (só a da frente recolhida).
  const showBubble = !behind && (fresh || hover || (fanned && index === 0));
  const color = head.sender.color;
  return (
    <div
      className="absolute left-0 flex items-center gap-2.5 transition-[bottom,transform,opacity] duration-300 ease-out"
      style={{
        bottom,
        zIndex: 10 - index,
        transform: behind ? `scale(${1 - index * 0.07})` : undefined,
        transformOrigin: "left center",
        opacity: behind && index > 2 ? 0 : 1,
      }}
      onPointerEnter={() => setHover(true)}
      onPointerLeave={() => setHover(false)}
    >
      <div className="group relative shrink-0 animate-chat-head-in">
        <button
          type="button"
          onClick={() => openConversation(head.conversationId)}
          className="block animate-chat-head-float rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring/60"
          style={{ animationDelay: `${(index % 3) * -1.1}s` }}
          aria-label={`Abrir a conversa com ${head.sender.name}: ${head.text}`}
        >
          <span
            className="block rounded-full p-[2.5px] shadow-[0_10px_26px_-6px_rgba(0,0,0,0.6)] transition-transform duration-200 group-hover:scale-105"
            style={{ background: color }}
          >
            <span className="block rounded-full bg-canvas p-[2px]">
              <Avatar name={head.sender.name} src={head.sender.avatarUrl} color={color} size={SIZE - 9} />
            </span>
          </span>
        </button>
        {head.count > 1 || !fresh ? (
          <span className="pointer-events-none absolute -right-1 -top-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-[var(--danger)] px-1 text-[10.5px] font-bold text-white ring-2 ring-canvas">
            {head.count > 9 ? "9+" : head.count}
          </span>
        ) : null}
        {!behind ? (
          <button
            type="button"
            onClick={() => dismiss(head.conversationId)}
            className="absolute -left-1 -top-1 hidden h-[18px] w-[18px] items-center justify-center rounded-full border border-border-strong bg-surface text-fg-muted shadow group-hover:flex hover:text-fg"
            aria-label="Fechar o balão"
          >
            <X className="h-3 w-3" />
          </button>
        ) : null}
      </div>
      <div
        role="button"
        tabIndex={showBubble ? 0 : -1}
        aria-hidden={!showBubble}
        onClick={() => openConversation(head.conversationId)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openConversation(head.conversationId);
          }
        }}
        className={cn(
          "group/bubble relative min-w-0 max-w-[280px] origin-left cursor-pointer rounded-2xl rounded-bl-md border border-border-strong bg-surface px-3.5 py-2.5 text-left shadow-[0_14px_40px_-12px_rgba(0,0,0,0.6)] outline-none transition-[opacity,transform] duration-300 ease-out hover:border-fg/30 focus-visible:ring-2 focus-visible:ring-ring/60",
          showBubble
            ? "translate-x-0 scale-100 opacity-100"
            : "pointer-events-none -translate-x-3 scale-90 opacity-0",
        )}
      >
        <span
          className="absolute -left-[6px] bottom-3 h-3 w-3 rotate-45 border-b border-l border-border-strong bg-surface"
          aria-hidden="true"
        />
        <div className="flex items-baseline gap-1.5 pr-7">
          <span className="truncate text-[12.5px] font-semibold" style={{ color }}>
            {head.sender.name}
          </span>
          {head.where ? <span className="truncate text-[11px] text-fg-subtle">em {head.where}</span> : null}
        </div>
        <p className="mt-0.5 line-clamp-2 text-[13px] leading-snug text-fg">{head.text}</p>
        <div className="absolute right-1.5 top-1.5 opacity-0 transition-opacity group-hover/bubble:opacity-100">
          <Tip label="Não mostrar mais balões (reative em Configurações › Notificações)">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setPrefs({ bubbles: false });
              }}
              className="flex h-6 w-6 items-center justify-center rounded-md text-fg-subtle hover:bg-bg-muted hover:text-fg"
              aria-label="Desativar balões de mensagem"
            >
              <BellOff className="h-3.5 w-3.5" />
            </button>
          </Tip>
        </div>
      </div>
    </div>
  );
}
